import { moneyToNumber, roundTo2, toMoney } from './decimal.js';
import { addMonths, periodRate, graceMonths } from './day-count.js';
import type { AmortizationInput, AmortizationResult, AmortizationRow } from './types.js';

/**
 * German method (capital fijo): constant principal payment per period,
 * interest decreases with the outstanding balance, so total installment
 * declines over time.
 *
 * Each period: principal = P / n, interest = balance * r, total = principal + interest.
 * The last installment absorbs the rounding remainder.
 */
export function amortizeGerman(input: AmortizationInput): AmortizationResult {
  const { principal, annualRate, termMonths, startDate, dayCount, roundingMode } = input;

  const P = moneyToNumber(principal);
  const r = Number(annualRate);
  if (!Number.isFinite(r) || r < 0) throw new Error(`invalid annual rate: ${annualRate}`);
  if (termMonths <= 0) throw new Error(`term must be positive: ${termMonths}`);

  const principalPerPeriod = P / termMonths;
  const g = graceMonths(input.interestStartMonth, termMonths);
  let prevDate = startDate;
  let balance = P;
  const rows: AmortizationRow[] = [];

  for (let i = 0; i < termMonths; i++) {
    const dueDate = addMonths(startDate, i + 1);
    const pr = i < g ? 0 : periodRate(dayCount, r, prevDate, dueDate);
    const interest = roundTo2(balance * pr, roundingMode);

    const isLast = i === termMonths - 1;
    const principalAmt = isLast
      ? roundTo2(balance, roundingMode)
      : roundTo2(principalPerPeriod, roundingMode);
    const totalAmt = roundTo2(principalAmt + interest, roundingMode);

    const opening = roundTo2(balance, roundingMode);
    const closing = roundTo2(balance - principalAmt, roundingMode);

    rows.push({
      installmentNumber: i + 1,
      dueDate,
      openingBalance: opening.toFixed(2),
      principalAmount: principalAmt.toFixed(2),
      interestAmount: interest.toFixed(2),
      totalAmount: totalAmt.toFixed(2),
      closingBalance: closing.toFixed(2),
    });

    balance = balance - principalAmt;
    prevDate = dueDate;
  }

  let totalPrincipal = 0;
  let totalInterest = 0;
  let totalPaid = 0;
  for (const row of rows) {
    totalPrincipal += moneyToNumber(row.principalAmount);
    totalInterest += moneyToNumber(row.interestAmount);
    totalPaid += moneyToNumber(row.totalAmount);
  }

  return {
    method: 'german',
    dayCount,
    totalPrincipal: toMoney(totalPrincipal, roundingMode),
    totalInterest: toMoney(totalInterest, roundingMode),
    totalPaid: toMoney(totalPaid, roundingMode),
    rows,
  };
}
